import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Form, Label, Loader, Message, Modal, Table } from 'semantic-ui-react'
import { X } from 'phosphor-react'
import styled from 'styled-components'
import { FlexRowBetween, TitleText } from 'components'
import { useNotification } from 'hooks/toast'
import { AuthService, MaintenanceService } from 'utility'
import {
    PRIORITY_COLORS,
    PRIORITY_OPTIONS,
    buildCompletionPayload,
    buildUpdatePayload,
    buildVerificationFields,
    completionFormFromDto,
    completionSummaryRows,
    formatDate,
    linkCandidates,
    maintenanceApiError,
    needsVerificationInspection,
    verificationFindingsText,
    verificationResultColor,
    verificationSettingsFromDto,
    verificationText,
} from './maintenance-utils'
import { VerificationSettings } from './VerificationSettings'
import { FindingEvidence } from './FindingEvidence'

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 10px 16px;
    margin-bottom: 16px;
    .label {
        color: #6b7280;
        font-size: 12px;
    }
    .value {
        font-size: 14px;
        color: #111827;
        word-break: break-word;
    }
`

const AssetCard = styled.div`
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 12px 14px;
    margin-bottom: 16px;
    background: #f9fafb;
    .name {
        font-weight: 600;
        font-size: 15px;
    }
    .meta {
        color: #6b7280;
        font-size: 13px;
        margin-top: 2px;
    }
`

const SectionTitle = styled.div`
    font-weight: 600;
    margin: 18px 0 8px;
`

const ROLE_KIND = { Source: 'source', PreInspection: 'pre', PostInspection: 'post' }

const linkedOf = (detail, role) => {
    if (!detail) return null
    if (role === 'Work') return detail.WorkInspection
    if (role === 'Source') return detail.SourceInspection
    if (role === 'PreInspection') return detail.PreInspection
    return detail.PostInspection
}

const formFromDetail = d => ({
    MaintenanceTypeId: d?.MaintenanceTypeId || '',
    PriorityId: d?.PriorityId || 2,
    Instructions: d?.Instructions || '',
    VerificationRequired: !!d?.VerificationRequired,
    CloseOnlyAfterVerification: !!d?.CloseOnlyAfterVerification,
    VerificationTemplateId: d?.VerificationTemplateId || '',
    ...verificationSettingsFromDto(d),
})

const Card = styled.div`
    border: 1px solid ${p => (p.$accent ? '#fcd34d' : '#e5e7eb')};
    border-radius: 6px;
    padding: 12px 14px;
    margin-bottom: 14px;
    .card-title {
        font-weight: 600;
        font-size: 15px;
        margin-bottom: 6px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
    }
    .meta {
        color: #6b7280;
        font-size: 13px;
    }
`

const SummaryList = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 4px 16px;
    font-size: 13px;
    .k {
        color: #6b7280;
        margin-right: 6px;
    }
    .v {
        white-space: pre-wrap;
        word-break: break-word;
    }
`

const PhotoColumns = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
    margin-top: 10px;
`

const ApprovalBox = styled.div`
    border: 1px solid #fcd34d;
    background: #fffbeb;
    border-radius: 6px;
    padding: 12px 14px;
    margin-bottom: 16px;
    .title {
        font-weight: 600;
        margin-bottom: 4px;
    }
    .meta {
        color: #6b7280;
        font-size: 13px;
    }
`

/**
 * Maintenance task detail (EAUDITOR-407): asset card, linked inspections (work checklist, source finding,
 * pre / post inspection) and - for admins and the creator (CanEdit) - type / priority / instructions / links.
 * Props: open, taskId, onClose(changed?).
 */
export const MaintenanceTaskDetailModal = ({ open, taskId, onClose, templateOptions = [], userOptions = [] }) => {
    const navigate = useNavigate()
    const { successToast, errorToast } = useNotification()
    const [detail, setDetail] = useState(null)
    const [loading, setLoading] = useState(false)
    const [loadError, setLoadError] = useState('')
    const [form, setForm] = useState(formFromDetail(null))
    const [types, setTypes] = useState([])
    const [saving, setSaving] = useState(false)
    const [actionError, setActionError] = useState('')
    const [changed, setChanged] = useState(false)
    const [assetAudits, setAssetAudits] = useState([])
    const [maintenanceTaskIds, setMaintenanceTaskIds] = useState([])
    const [linkKind, setLinkKind] = useState('pre')
    const [linkValue, setLinkValue] = useState('')
    const [decisionNote, setDecisionNote] = useState('')
    const [formErrors, setFormErrors] = useState({})
    const [completion, setCompletion] = useState(completionFormFromDto(null))
    const [completionErrors, setCompletionErrors] = useState({})
    const [assetStatus, setAssetStatus] = useState('')

    // the task this modal currently shows - answers for an earlier task are dropped
    const currentTaskRef = useRef(taskId)
    currentTaskRef.current = taskId

    const load = useCallback(async () => {
        if (!taskId) return
        setLoading(true)
        setLoadError('')
        try {
            const result = await MaintenanceService.get(taskId)
            if (currentTaskRef.current !== taskId) return
            setDetail(result)
            setForm(formFromDetail(result))
            setCompletion(completionFormFromDto(result?.Completion))
            setCompletionErrors({})
            if (result?.CanEdit && result?.Asset?.AssetId) {
                const me = AuthService.getUser()
                MaintenanceService.assetAudits(me?.CompanyId, result.Asset.AssetId)
                    .then(page => {
                        if (currentTaskRef.current !== taskId) return
                        setAssetAudits(page?.Items || page?.items || (Array.isArray(page) ? page : []))
                    })
                    .catch(() => setAssetAudits([]))
                MaintenanceService.byAsset(result.Asset.AssetId)
                    .then(list => {
                        if (currentTaskRef.current !== taskId) return
                        setMaintenanceTaskIds((Array.isArray(list) ? list : []).map(x => x.TaskId))
                    })
                    .catch(() => setMaintenanceTaskIds([]))
            }
        } catch (error) {
            if (currentTaskRef.current !== taskId) return
            setDetail(null)
            setLoadError(maintenanceApiError(error, 'The maintenance task could not be loaded.'))
        } finally {
            if (currentTaskRef.current === taskId) setLoading(false)
        }
    }, [taskId])

    useEffect(() => {
        if (!open) return
        // never show (or act on) the previous task while this one loads
        setDetail(null)
        setAssetAudits([])
        setMaintenanceTaskIds([])
        setChanged(false)
        setActionError('')
        setLinkValue('')
        setDecisionNote('')
        setAssetStatus('')
        setFormErrors({})
        load()
        MaintenanceService.types()
            .then(result => setTypes(Array.isArray(result) ? result : []))
            .catch(() => setTypes([]))
    }, [open, load])

    const typeOptions = useMemo(
        () => [
            { key: 0, value: '', text: 'Not set' },
            ...types.map(t => ({ key: t.MaintenanceTypeId, value: t.MaintenanceTypeId, text: t.Name })),
        ],
        [types],
    )

    const candidates = useMemo(
        () => linkCandidates(assetAudits, linkKind, detail, maintenanceTaskIds),
        [assetAudits, linkKind, detail, maintenanceTaskIds],
    )

    const close = () => {
        if (saving || !discardCompletionOk()) return
        onClose(changed)
    }

    // typed completion values are lost when the modal reloads or navigates away
    const discardCompletionOk = () =>
        !detail ||
        JSON.stringify(completion) === JSON.stringify(completionFormFromDto(detail.Completion)) ||
        // eslint-disable-next-line no-alert
        window.confirm('The completion card has unsaved changes. Leave without saving them?')

    const openAudit = linked => {
        if (!linked?.AuditTemplateId) return
        if (!discardCompletionOk()) return
        onClose(changed)
        navigate(`/app/templates/inspection/${linked.AuditTemplateId}`)
    }

    const save = async () => {
        if (!discardCompletionOk()) return
        // an invalid pass mark / self approver must not be sent (it would clear the stored value)
        const found = form.VerificationRequired ? buildVerificationFields({ ...form, AssigneeUserId: detail?.AssigneeUserId }).errors : {}
        setFormErrors(found)
        if (Object.values(found).some(Boolean)) return
        const body = buildUpdatePayload(detail, form)
        if (!Object.keys(body).length) return
        setSaving(true)
        setActionError('')
        try {
            await MaintenanceService.update(taskId, body)
            successToast('Maintenance task saved')
            setChanged(true)
            await load()
        } catch (error) {
            const message = maintenanceApiError(error)
            setActionError(message)
            // base.js already toasts role refusals
            if (!error?.roleRefusalHandled) errorToast(message)
        } finally {
            setSaving(false)
        }
    }

    const link = async (kind, remove, value) => {
        const body = { Kind: kind, Remove: !!remove }
        if (!remove) {
            if (kind === 'source') body.SourceAuditId = value
            else body.TaskId = value
        }
        setSaving(true)
        setActionError('')
        try {
            await MaintenanceService.link(taskId, body)
            successToast(remove ? 'Link removed' : 'Inspection linked')
            setChanged(true)
            setLinkValue('')
            await load()
        } catch (error) {
            const message = maintenanceApiError(error)
            setActionError(message)
            // base.js already toasts role refusals
            if (!error?.roleRefusalHandled) errorToast(message)
        } finally {
            setSaving(false)
        }
    }

    // Approve & Close / Reject (EAUDITOR-410): the reason is required for a rejection
    const decide = async approve => {
        if (!discardCompletionOk()) return
        const note = decisionNote.trim()
        if (!approve && !note) {
            setActionError('Give the reason for the rejection.')
            return
        }
        setSaving(true)
        setActionError('')
        try {
            const body = approve ? { Notes: note || null, AssetStatus: assetStatus.trim() || null } : { Reason: note }
            if (approve) await MaintenanceService.approve(taskId, body)
            else await MaintenanceService.reject(taskId, body)
            successToast(approve ? 'Work approved - the task is closed' : 'Work rejected - returned to the technician')
            setChanged(true)
            setDecisionNote('')
            setAssetStatus('')
            await load()
        } catch (error) {
            const message = maintenanceApiError(error)
            setActionError(message)
            if (!error?.roleRefusalHandled) errorToast(message)
        } finally {
            setSaving(false)
        }
    }

    // main button(s) of the technician / verifier (EAUDITOR-409): server AllowedActions; Approve / Reject live in the approval card
    const runAction = async action => {
        if (action.Action === 'Accept') {
            if (!discardCompletionOk()) return
            setSaving(true)
            setActionError('')
            try {
                await MaintenanceService.accept(taskId)
                successToast('Task accepted')
                setChanged(true)
                await load()
            } catch (error) {
                const message = maintenanceApiError(error)
                setActionError(message)
                if (!error?.roleRefusalHandled) errorToast(message)
            } finally {
                setSaving(false)
            }
            return
        }
        if (action.AuditTemplateId) openAudit(action)
    }

    const saveCompletion = async () => {
        const { errors: found, payload } = buildCompletionPayload(completion)
        setCompletionErrors(found)
        if (Object.values(found).some(Boolean)) return
        setSaving(true)
        setActionError('')
        try {
            await MaintenanceService.saveCompletion(taskId, payload)
            successToast('Completion saved')
            setChanged(true)
            await load()
        } catch (error) {
            const message = maintenanceApiError(error)
            setActionError(message)
            if (!error?.roleRefusalHandled) errorToast(message)
        } finally {
            setSaving(false)
        }
    }

    const workActions = (detail?.AllowedActions || []).filter(a => a.Action !== 'Approve' && a.Action !== 'Reject')
    const completionDirty = detail && JSON.stringify(completion) !== JSON.stringify(completionFormFromDto(detail.Completion))

    const setField = (field, value) => {
        setForm(f => ({ ...f, [field]: value }))
        setFormErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev))
    }

    // the server refuses verification changes by the technician (their own work) and on a closed task
    const me = AuthService.getUser()
    const verificationLocked =
        !!detail && (Number(detail.AssigneeUserId) === Number(me?.unique_id || me?.UserId) || Number(detail.Stage) === 6)

    const dirty = detail && Object.keys(buildUpdatePayload(detail, form)).length > 0
    const asset = detail?.Asset

    // one linked inspection (source / pre / work / verification) inside its card
    const linkedBlock = (role, emptyText = 'Not linked') => {
        const linked = linkedOf(detail, role)
        if (!linked) return <div className="meta">{emptyText}</div>
        return (
            <>
                <div>
                    {linked.IsHidden ? (
                        <span className="meta">Linked inspection (not visible to you)</span>
                    ) : (
                        linked.AuditName || linked.TemplateName
                    )}
                </div>
                {!linked.IsHidden && (
                    <div className="meta">
                        {[
                            linked.AuditStatusName,
                            linked.AssigneeName && `Assigned to ${linked.AssigneeName}`,
                            linked.CompletedOn && `Completed ${formatDate(linked.CompletedOn)}`,
                        ]
                            .filter(Boolean)
                            .join(' · ')}
                    </div>
                )}
                {linked.QuestionText && (
                    <div style={{ fontSize: 13, marginTop: 4 }}>
                        Finding: {linked.QuestionText}
                        {linked.AnswerText ? ` - ${linked.AnswerText}` : ''}
                    </div>
                )}
                {linked.TicketId && <div className="meta">Ticket #{linked.TicketId}</div>}
                {role === 'Source' && <FindingEvidence notes={linked.Notes} photos={linked.Photos} size={56} />}
                {(linked.AuditTemplateId || (role !== 'Work' && detail.CanEdit)) && (
                    <div style={{ marginTop: 8 }}>
                        {linked.AuditTemplateId && (
                            <Button size="mini" basic onClick={() => openAudit(linked)}>
                                Open
                            </Button>
                        )}
                        {role !== 'Work' && detail.CanEdit && (
                            <Button size="mini" basic color="red" disabled={saving} onClick={() => link(ROLE_KIND[role], true)}>
                                Remove
                            </Button>
                        )}
                    </div>
                )}
            </>
        )
    }

    // Verification card (EAUDITOR-410): how the work is verified, what the technician did (summary + before / after
    // photos), the verification inspection, the approval and every attempt. Shown first to an approver while it waits.
    const sourcePhotos = detail?.SourceInspection?.Photos || []
    const workPhotos = detail?.WorkPhotos || []
    const verificationCard =
        detail && (detail.VerificationRequired || detail.PostInspection || (detail.Verifications || []).length > 0) ? (
            <Card $accent={detail.ApprovalPending}>
                <div className="card-title">
                    Verification
                    <Label size="small" basic>
                        {verificationText(detail)}
                    </Label>
                </div>
                <div className="meta">
                    {[
                        detail.VerifierName && `Verified by ${detail.VerifierName}`,
                        (detail.VerificationMethod === 2 || detail.VerificationMethod === 3) &&
                            `Approved by ${detail.ApproverName || 'any company admin'}`,
                        detail.SeparationOfDuty && 'Separation of duty on',
                        detail.VerificationPassMark != null && `Pass mark ${detail.VerificationPassMark}%`,
                        detail.VerificationTemplateName,
                    ]
                        .filter(Boolean)
                        .join(' · ')}
                </div>
                <SectionTitle style={{ marginTop: 12 }}>Work done</SectionTitle>
                <SummaryList>
                    {completionSummaryRows(detail).map(r => (
                        <div key={r.label}>
                            <span className="k">{r.label}</span>
                            <span className="v">{r.value}</span>
                        </div>
                    ))}
                </SummaryList>
                {(sourcePhotos.length > 0 || workPhotos.length > 0) && (
                    <PhotoColumns>
                        <div>
                            <div className="meta">Before (finding)</div>
                            {sourcePhotos.length ? <FindingEvidence photos={sourcePhotos} /> : <div className="meta">-</div>}
                        </div>
                        <div>
                            <div className="meta">After (work checklist)</div>
                            {workPhotos.length ? <FindingEvidence photos={workPhotos} /> : <div className="meta">-</div>}
                        </div>
                    </PhotoColumns>
                )}
                {(detail.PostInspection || needsVerificationInspection(detail.VerificationMethod)) && (
                    <>
                        <SectionTitle style={{ marginTop: 12 }}>Verification inspection</SectionTitle>
                        {linkedBlock(
                            'PostInspection',
                            detail.VerificationTemplateId
                                ? 'Created when the work checklist is completed'
                                : 'No verification template - link a verification inspection below',
                        )}
                    </>
                )}
                {detail.ApprovalPending && (
                    <ApprovalBox>
                        <div className="title">Waiting for manager approval</div>
                        <div className="meta">
                            {detail.ApproverName ? `Approver: ${detail.ApproverName} (or a company admin)` : 'Any company admin can approve.'}
                        </div>
                        {detail.CanApprove && (
                            <Form style={{ marginTop: 10 }}>
                                <Form.TextArea
                                    label="Notes (required to reject)"
                                    rows={2}
                                    maxLength={1000}
                                    value={decisionNote}
                                    onChange={(e, { value }) => setDecisionNote(value)}
                                />
                                <Form.Input
                                    label="Set the asset status when closing (optional)"
                                    placeholder={asset?.Status ? `Now: ${asset.Status}` : 'e.g. Active'}
                                    maxLength={50}
                                    value={assetStatus}
                                    onChange={(e, { value }) => setAssetStatus(value)}
                                />
                                <Button type="button" primary size="small" disabled={saving} onClick={() => decide(true)}>
                                    Approve &amp; Close
                                </Button>
                                <Button type="button" basic color="red" size="small" disabled={saving} onClick={() => decide(false)}>
                                    Reject / Return to technician
                                </Button>
                            </Form>
                        )}
                    </ApprovalBox>
                )}
                {Array.isArray(detail.Verifications) && detail.Verifications.length > 0 && (
                    <>
                        <SectionTitle style={{ marginTop: 12 }}>Attempts</SectionTitle>
                        <Table compact celled unstackable>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>Attempt</Table.HeaderCell>
                                    <Table.HeaderCell>Step</Table.HeaderCell>
                                    <Table.HeaderCell>Result</Table.HeaderCell>
                                    <Table.HeaderCell>Inspection / decision</Table.HeaderCell>
                                    <Table.HeaderCell>Decided</Table.HeaderCell>
                                    <Table.HeaderCell />
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {detail.Verifications.map(v => (
                                    <Table.Row key={v.Attempt}>
                                        <Table.Cell collapsing>#{v.Attempt}</Table.Cell>
                                        <Table.Cell collapsing>{v.MethodName || 'Verification inspection'}</Table.Cell>
                                        <Table.Cell collapsing>
                                            <Label size="small" color={verificationResultColor(v.Result)}>
                                                {v.ResultName || 'Pending'}
                                            </Label>
                                        </Table.Cell>
                                        <Table.Cell>
                                            {v.Method === 2 && (
                                                <>
                                                    {v.VerifiedByName || '-'}
                                                    {v.FindingSummary && (
                                                        <div style={{ color: '#374151', fontSize: 12, whiteSpace: 'pre-wrap' }}>{v.FindingSummary}</div>
                                                    )}
                                                </>
                                            )}
                                            {v.Method !== 2 && v.IsHidden && (
                                                <span style={{ color: '#6b7280' }}>Inspection not visible to you</span>
                                            )}
                                            {v.Method !== 2 && !v.IsHidden && (
                                                <>
                                                    {v.AuditName || '-'}
                                                    {v.VerifiedByName && (
                                                        <span style={{ color: '#6b7280' }}> - {v.VerifiedByName}</span>
                                                    )}
                                                </>
                                            )}
                                            {v.Method !== 2 && v.FindingSummary && (
                                                <div style={{ color: '#b91c1c', fontSize: 12 }}>
                                                    {verificationFindingsText(v)}
                                                </div>
                                            )}
                                        </Table.Cell>
                                        <Table.Cell collapsing>{v.ActedOnUtc ? formatDate(v.ActedOnUtc) : '-'}</Table.Cell>
                                        <Table.Cell collapsing>
                                            {v.AuditTemplateId && (
                                                <Button size="mini" basic onClick={() => openAudit(v)}>
                                                    Open
                                                </Button>
                                            )}
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </>
                )}
            </Card>
        ) : null
    const verificationFirst = !!detail?.ApprovalPending && !!detail?.CanApprove

    return (
        <Modal open={open} onClose={close} size="large">
            <Modal.Header>
                <FlexRowBetween>
                    <TitleText>
                        {detail?.Title || 'Maintenance task'}{' '}
                        <Label size="small" color="teal" style={{ marginLeft: 8 }}>
                            Maintenance
                        </Label>
                    </TitleText>
                    <X size={20} className="pointer" onClick={close} />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content scrolling>
                {loading && !detail && <Loader active inline="centered" />}
                {loadError && <Message negative content={loadError} />}
                {detail && (
                    <>
                        <Grid>
                            <div>
                                <div className="label">Status</div>
                                <div className="value">{detail.TaskStatusName || '-'}</div>
                            </div>
                            <div>
                                <div className="label">Stage</div>
                                <div className="value">{detail.StageName || '-'}</div>
                            </div>
                            <div>
                                <div className="label">Priority</div>
                                <div className="value" style={{ color: PRIORITY_COLORS[detail.PriorityId] }}>
                                    {detail.PriorityName || '-'}
                                </div>
                            </div>
                            <div>
                                <div className="label">Maintenance type</div>
                                <div className="value">{detail.MaintenanceTypeName || 'Not set'}</div>
                            </div>
                            <div>
                                <div className="label">Due</div>
                                <div className="value">{formatDate(detail.TaskDue)}</div>
                            </div>
                            <div>
                                <div className="label">Assigned to</div>
                                <div className="value">{detail.AssigneeName || '-'}</div>
                            </div>
                            <div>
                                <div className="label">Department / customer</div>
                                <div className="value">{detail.CustomerName || '-'}</div>
                            </div>
                            <div>
                                <div className="label">Verification</div>
                                <div className="value">
                                    {verificationText(detail)}
                                    {detail.VerificationTemplateName && (
                                        <div style={{ color: '#6b7280', fontSize: 12 }}>{detail.VerificationTemplateName}</div>
                                    )}
                                </div>
                            </div>
                            {detail.MaintenanceScheduleName && (
                                <div>
                                    <div className="label">Schedule</div>
                                    <div className="value">{detail.MaintenanceScheduleName}</div>
                                </div>
                            )}
                        </Grid>

                        {workActions.length > 0 && (
                            <div style={{ margin: '0 0 16px' }}>
                                {workActions.map((a, i) => (
                                    <Button
                                        key={a.Action}
                                        type="button"
                                        size="small"
                                        primary={i === 0}
                                        basic={i > 0}
                                        disabled={saving}
                                        onClick={() => runAction(a)}
                                    >
                                        {a.Label}
                                    </Button>
                                ))}
                            </div>
                        )}

                        {verificationFirst && verificationCard}

                        {asset && (
                            <AssetCard>
                                <div className="name">
                                    {asset.AssetName}
                                    {asset.AssetTag ? ` (${asset.AssetTag})` : ''}
                                    {!asset.IsActive && (
                                        <Label size="mini" color="grey" style={{ marginLeft: 8 }}>
                                            Inactive
                                        </Label>
                                    )}
                                </div>
                                <div className="meta">
                                    {[asset.CategoryName, asset.LocationName, asset.SerialNumber && `S/N ${asset.SerialNumber}`, asset.Status]
                                        .filter(Boolean)
                                        .join(' · ') || 'No further details'}
                                </div>
                                <Button
                                    size="mini"
                                    basic
                                    style={{ marginTop: 8 }}
                                    onClick={() => {
                                        onClose(changed)
                                        navigate(`/app/asset-management/${asset.AssetId}`)
                                    }}
                                >
                                    Open asset
                                </Button>
                            </AssetCard>
                        )}

                        {detail.SourceInspection && (
                            <Card>
                                <div className="card-title">Source finding</div>
                                {linkedBlock('Source')}
                            </Card>
                        )}

                        {detail.PreInspection && (
                            <Card>
                                <div className="card-title">Pre-inspection</div>
                                {linkedBlock('PreInspection')}
                            </Card>
                        )}

                        <Card>
                            <div className="card-title">Work</div>
                            {linkedBlock('Work', 'No work checklist')}
                            {!detail.CanEdit && detail.Instructions && (
                                <>
                                    <SectionTitle>Instructions</SectionTitle>
                                    <div style={{ whiteSpace: 'pre-wrap' }}>{detail.Instructions}</div>
                                </>
                            )}
                            <SectionTitle>Completion</SectionTitle>
                            <Form>
                                <Form.TextArea
                                    label="Work performed"
                                    rows={2}
                                    maxLength={2000}
                                    disabled={!detail.CanEditCompletion}
                                    value={completion.WorkPerformed}
                                    onChange={(e, { value }) => setCompletion(c => ({ ...c, WorkPerformed: value }))}
                                />
                                <Form.Input
                                    label="Parts used"
                                    maxLength={1000}
                                    disabled={!detail.CanEditCompletion}
                                    value={completion.PartsUsed}
                                    onChange={(e, { value }) => setCompletion(c => ({ ...c, PartsUsed: value }))}
                                />
                                <Form.Group widths="equal">
                                    <Form.Input
                                        label="Labour (hours)"
                                        type="number"
                                        min={0}
                                        step="0.25"
                                        disabled={!detail.CanEditCompletion}
                                        value={completion.LabourHours}
                                        onChange={(e, { value }) => setCompletion(c => ({ ...c, LabourHours: value }))}
                                        error={completionErrors.LabourHours ? { content: completionErrors.LabourHours } : undefined}
                                    />
                                    <Form.Input
                                        label="Cost"
                                        type="number"
                                        min={0}
                                        step="0.01"
                                        disabled={!detail.CanEditCompletion}
                                        value={completion.Cost}
                                        onChange={(e, { value }) => setCompletion(c => ({ ...c, Cost: value }))}
                                        error={completionErrors.Cost ? { content: completionErrors.Cost } : undefined}
                                    />
                                    <Form.Input
                                        label="Downtime (hours)"
                                        type="number"
                                        min={0}
                                        step="0.25"
                                        disabled={!detail.CanEditCompletion}
                                        value={completion.DowntimeHours}
                                        onChange={(e, { value }) => setCompletion(c => ({ ...c, DowntimeHours: value }))}
                                        error={completionErrors.DowntimeHours ? { content: completionErrors.DowntimeHours } : undefined}
                                    />
                                </Form.Group>
                                <Form.TextArea
                                    label="Completion notes"
                                    rows={2}
                                    maxLength={2000}
                                    disabled={!detail.CanEditCompletion}
                                    value={completion.CompletionNotes}
                                    onChange={(e, { value }) => setCompletion(c => ({ ...c, CompletionNotes: value }))}
                                />
                                {detail.Completion?.UpdatedOnUtc && (
                                    <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 8 }}>
                                        Last saved by {detail.Completion.UpdatedByName || '-'} on {formatDate(detail.Completion.UpdatedOnUtc)}
                                    </div>
                                )}
                                {detail.CanEditCompletion && (
                                    <Button type="button" size="small" disabled={!completionDirty || saving} onClick={saveCompletion}>
                                        Save completion
                                    </Button>
                                )}
                            </Form>
                        </Card>

                        {!verificationFirst && verificationCard}

                        {detail.CanEdit && (
                            <Form>
                                <Form.Group widths="equal">
                                    <Form.Dropdown
                                        label="Link an inspection of this asset as"
                                        selection
                                        options={[
                                            { key: 'pre', value: 'pre', text: 'Pre-inspection' },
                                            { key: 'post', value: 'post', text: 'Post-inspection (verification)' },
                                            { key: 'source', value: 'source', text: 'Source inspection' },
                                        ]}
                                        value={linkKind}
                                        onChange={(e, { value }) => {
                                            setLinkKind(value)
                                            setLinkValue('')
                                        }}
                                    />
                                    <Form.Dropdown
                                        label="Inspection"
                                        selection
                                        search
                                        placeholder={candidates.length ? 'Select an inspection' : 'No inspections of this asset to link'}
                                        options={candidates}
                                        value={linkValue}
                                        onChange={(e, { value }) => setLinkValue(value)}
                                    />
                                </Form.Group>
                                <Button
                                    type="button"
                                    size="small"
                                    disabled={!linkValue || saving}
                                    onClick={() => link(linkKind, false, linkValue)}
                                >
                                    Link
                                </Button>

                                <SectionTitle>Details</SectionTitle>
                                <Form.Group widths="equal">
                                    <Form.Dropdown
                                        label="Maintenance type"
                                        selection
                                        placeholder="Not set"
                                        options={typeOptions}
                                        value={form.MaintenanceTypeId}
                                        onChange={(e, { value }) => setForm(f => ({ ...f, MaintenanceTypeId: value }))}
                                    />
                                    <Form.Dropdown
                                        label="Priority"
                                        selection
                                        options={PRIORITY_OPTIONS}
                                        value={form.PriorityId}
                                        onChange={(e, { value }) => setForm(f => ({ ...f, PriorityId: value }))}
                                    />
                                </Form.Group>
                                <Form.TextArea
                                    label="Instructions"
                                    rows={3}
                                    maxLength={2000}
                                    value={form.Instructions}
                                    onChange={(e, { value }) => setForm(f => ({ ...f, Instructions: value }))}
                                />
                                <Form.Checkbox
                                    label="Verification required after the work"
                                    disabled={verificationLocked}
                                    checked={form.VerificationRequired}
                                    onChange={(e, { checked }) => setField('VerificationRequired', checked)}
                                />
                                {verificationLocked && (
                                    <div style={{ color: '#6b7280', fontSize: 12, margin: '-8px 0 12px' }}>
                                        {Number(detail.Stage) === 6
                                            ? 'The task is closed - its verification can no longer be changed.'
                                            : 'You do this work, so you cannot change how it is verified.'}
                                    </div>
                                )}
                                {form.VerificationRequired && (
                                    <VerificationSettings
                                        form={form}
                                        set={setField}
                                        templateOptions={templateOptions}
                                        userOptions={userOptions}
                                        errors={formErrors}
                                        disabled={verificationLocked}
                                    />
                                )}
                            </Form>
                        )}
                        {actionError && <Message negative content={actionError} />}
                    </>
                )}
            </Modal.Content>
            <Modal.Actions>
                <Button type="button" onClick={close} disabled={saving}>
                    Close
                </Button>
                {detail?.CanEdit && (
                    <Button type="button" primary onClick={save} loading={saving} disabled={!dirty || saving}>
                        Save
                    </Button>
                )}
            </Modal.Actions>
        </Modal>
    )
}
